import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ServiceConfigurator from './components/ServiceConfigurator';
import ServiceCard from './components/ServiceCard';
import PricingTransparency from './components/PricingTransparency';
import { trackServiceEngagement, trackCTAClick } from '../../utils/analyticsEvents';

const ServicesOverview = () => {
  const navigate = useNavigate();
  const [expandedService, setExpandedService] = useState(null);

  const handleServiceExpand = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
    if (expandedService !== serviceId) {
      trackServiceEngagement(serviceId, 'expand');
    }
  };

  const handleCTAClick = (ctaName) => {
    trackCTAClick(ctaName, 'Services Overview');
  };

  const services = [
    {
      id: 'data-strategy',
      title: 'Data Strategy & Governance',
      subtitle: 'Transform data chaos into strategic advantage',
      description: 'Maturity assessments, GDPR/AI Act compliance frameworks, and data policies designed for SMEs in finance, retail, healthcare, and logistics.',
      icon: 'Target',
      startingPrice: 15000,
      duration: '6-8 weeks',
      tags: ['Strategy', 'GDPR Compliance', 'Data Maturity'],
      capabilities: [
        {
          title: 'Data Maturity Assessment',
          description: 'Comprehensive evaluation of current data capabilities and readiness for transformation'
        },
        {
          title: 'GDPR & AI Act Compliance',
          description: 'Complete compliance frameworks ensuring regulatory adherence and risk mitigation'
        },
        {
          title: 'Strategic Roadmap Creation',
          description: 'Step-by-step implementation plan with priorities, timelines, and resource requirements'
        },
        {
          title: 'Business Value Alignment',
          description: 'Ensuring every data initiative delivers measurable ROI and business impact'
        }
      ],
      timeline: [
        {
          phase: 'Discovery & Assessment',
          description: 'Stakeholder interviews, data audit, current state analysis',
          duration: '2 weeks'
        },
        {
          phase: 'Strategy Development',
          description: 'Roadmap creation, technology planning, resource allocation',
          duration: '3 weeks'
        },
        {
          phase: 'Validation & Refinement',
          description: 'Stakeholder review, strategy refinement, final documentation',
          duration: '1-2 weeks'
        }
      ],
      outcomes: [
        {
          icon: 'TrendingUp',
          metric: 'ROI Improvement',
          value: '300%+',
          description: 'Average ROI increase within 12 months'
        },
        {
          icon: 'Clock',
          metric: 'Time to Value',
          value: '60% faster',
          description: 'Reduced implementation time with clear roadmap'
        }
      ],
      caseStudy: {
        quote: 'The strategic roadmap transformed our approach to data. We went from reactive to proactive, with clear priorities and measurable outcomes.',
        client: 'Sarah Johnson',
        company: 'TechFlow Solutions'
      }
    },
    {
      id: 'cloud-migration',
      title: 'Cloud Data Warehousing & Migration',
      subtitle: 'Vendor-neutral cloud solutions',
      description: 'Expert guidance for Snowflake, BigQuery, Azure Synapse, and AWS Redshift with education-first approach for sustainable implementation.',
      icon: 'Cloud',
      startingPrice: 25000,
      duration: '8-12 weeks',
      tags: ['Cloud Migration', 'Vendor-Neutral', 'Training'],
      capabilities: [
        {
          title: 'Platform Selection & Setup',
          description: 'Vendor-neutral evaluation of Snowflake, BigQuery, Azure Synapse, AWS Redshift'
        },
        {
          title: 'Migration Strategy & Execution',
          description: 'Complete data migration with minimal downtime and risk mitigation'
        },
        {
          title: 'Performance Optimization',
          description: 'Ongoing optimization for cost efficiency and query performance'
        },
        {
          title: 'Team Training & Knowledge Transfer',
          description: 'Education-first approach ensuring internal capability building'
        }
      ],
      timeline: [
        {
          phase: 'Platform Setup & Configuration',
          description: 'Environment configuration, security setup, initial data connections',
          duration: '2-3 weeks'
        },
        {
          phase: 'Data Pipeline Development',
          description: 'ETL processes, data modeling, quality assurance implementation',
          duration: '4-5 weeks'
        },
        {
          phase: 'Testing & Optimization',
          description: 'Performance tuning, data validation, security verification',
          duration: '2-3 weeks'
        },
        {
          phase: 'Training & Handover',
          description: 'User training, documentation, support procedures establishment',
          duration: '1-2 weeks'
        }
      ],
      outcomes: [
        {
          icon: 'Zap',
          metric: 'Query Performance',
          value: '90% faster',
          description: 'Improved query execution vs legacy systems'
        },
        {
          icon: 'DollarSign',
          metric: 'Infrastructure Costs',
          value: '40% reduction',
          description: 'Optimized cloud resource utilization'
        }
      ],
      caseStudy: {
        quote: 'The cloud migration transformed our data capabilities. We now have real-time insights and the scalability to grow without infrastructure constraints.',
        client: 'David Martinez',
        company: 'ScaleUp Retail'
      }
    },
    {
      id: 'business-intelligence',
      title: 'Business Intelligence & Visualization',
      subtitle: 'Power BI, Tableau, Looker implementation',
      description: 'Comprehensive BI platform deployment with self-service frameworks, enabling data-driven decision making across your organization.',
      icon: 'BarChart3',
      startingPrice: 18000,
      duration: '6-10 weeks',
      tags: ['Power BI', 'Tableau', 'Self-Service BI'],
      capabilities: [
        {
          title: 'Platform Implementation',
          description: 'Power BI, Tableau, or Looker setup with best-practice configurations'
        },
        {
          title: 'Dashboard & KPI Development',
          description: 'Executive and operational dashboards with real-time business insights'
        },
        {
          title: 'Self-Service BI Framework',
          description: 'Empowering business users to create their own reports and analyses'
        },
        {
          title: 'Data Storytelling Training',
          description: 'Building internal capability for effective data communication'
        }
      ],
      timeline: [
        {
          phase: 'Platform Setup & Data Integration',
          description: 'Environment configuration, data source connectivity, security setup',
          duration: '2-3 weeks'
        },
        {
          phase: 'Dashboard Development',
          description: 'Report creation, visualization design, KPI implementation',
          duration: '3-4 weeks'
        },
        {
          phase: 'User Training & Enablement',
          description: 'Self-service training, documentation, governance framework',
          duration: '2-3 weeks'
        }
      ],
      outcomes: [
        {
          icon: 'Clock',
          metric: 'Report Generation',
          value: '85% faster',
          description: 'Automated reporting vs manual processes'
        },
        {
          icon: 'Eye',
          metric: 'Data Accessibility',
          value: '100%',
          description: 'Real-time visibility across all business metrics'
        }
      ],
      caseStudy: {
        quote: 'Our analytics implementation eliminated hours of manual reporting and gave us insights we never had before. Decision-making is now data-driven across the organization.',
        client: 'Michael Chen',
        company: 'InnovateCorp'
      }
    },
    {
      id: 'applied-ml',
      title: 'Applied Machine Learning',
      subtitle: 'Customer churn, forecasting, recommendations',
      description: 'Practical ML applications including customer churn prediction, business forecasting, and recommender systems designed for mid-cap business operations.',
      icon: 'Brain',
      startingPrice: 22000,
      duration: '10-16 weeks',
      tags: ['ML Applications', 'Predictive Analytics', 'Business Value'],
      capabilities: [
        {
          title: 'Customer Churn Prediction',
          description: 'ML models to identify at-risk customers and recommend retention strategies'
        },
        {
          title: 'Business Forecasting Systems',
          description: 'Demand forecasting, sales prediction, and capacity planning models'
        },
        {
          title: 'Recommender Systems',
          description: 'Product and content recommendation engines for enhanced customer experience'
        },
        {
          title: 'Model Governance & Monitoring',
          description: 'MLOps framework ensuring model performance and regulatory compliance'
        }
      ],
      timeline: [
        {
          phase: 'Data Preparation & Analysis',
          description: 'Data quality assessment, feature engineering, exploratory analysis',
          duration: '3-4 weeks'
        },
        {
          phase: 'Model Development & Training',
          description: 'Algorithm selection, model training, validation, and optimization',
          duration: '4-6 weeks'
        },
        {
          phase: 'Deployment & Integration',
          description: 'Production deployment, API development, system integration',
          duration: '2-4 weeks'
        },
        {
          phase: 'Monitoring & Maintenance',
          description: 'Performance monitoring, model retraining, ongoing support',
          duration: '1-2 weeks'
        }
      ],
      outcomes: [
        {
          icon: 'TrendingUp',
          metric: 'Customer Retention',
          value: '25% increase',
          description: 'Improved retention through predictive insights'
        },
        {
          icon: 'Target',
          metric: 'Forecast Accuracy',
          value: '90%+',
          description: 'Enhanced business planning and resource allocation'
        }
      ],
      caseStudy: {
        quote: 'The ML models transformed our customer relationships. We can now predict and prevent churn while optimizing our inventory based on accurate demand forecasts.',
        client: 'Amanda Thompson',
        company: 'GrowthTech Solutions'
      }
    },
    {
      id: 'training-workshops',
      title: 'Training & Capability Building',
      subtitle: 'Custom workshops and upskilling',
      description: 'Education-first training programs that bridge consulting with capability building, upskilling internal teams for sustainable data adoption and growth.',
      icon: 'Users',
      startingPrice: 8000,
      duration: '4-6 weeks',
      tags: ['Training', 'Workshops', 'Capability Building'],
      capabilities: [
        {
          title: 'Skills Assessment & Gap Analysis',
          description: 'Comprehensive evaluation of current team capabilities and development needs'
        },
        {
          title: 'Customized Training Curriculum',
          description: 'Tailored workshops addressing specific organizational needs and skill gaps'
        },
        {
          title: 'Hands-on Workshop Delivery',
          description: 'Practical training sessions with real-world scenarios and case studies'
        },
        {
          title: 'Ongoing Mentorship Support',
          description: 'Continuous guidance and coaching for sustained skill development'
        }
      ],
      timeline: [
        {
          phase: 'Assessment & Planning',
          description: 'Skills evaluation, curriculum design, resource preparation',
          duration: '1 week'
        },
        {
          phase: 'Core Training Delivery',
          description: 'Workshop sessions, practical exercises, knowledge transfer',
          duration: '2-3 weeks'
        },
        {
          phase: 'Mentoring & Support',
          description: 'Ongoing coaching, progress evaluation, additional support',
          duration: '1-2 weeks'
        }
      ],
      outcomes: [
        {
          icon: 'TrendingUp',
          metric: 'Skill Improvement',
          value: '400%',
          description: 'Average increase in data literacy scores'
        },
        {
          icon: 'Users',
          metric: 'Team Confidence',
          value: '85%+',
          description: 'Improvement in data decision-making confidence'
        }
      ],
      caseStudy: {
        quote: 'The training program transformed our team\'s relationship with data. We went from being intimidated by analytics to confidently making data-driven decisions every day.',
        client: 'Jennifer Walsh',
        company: 'DataFirst Manufacturing'
      }
    }
  ];

  const handleServiceToggle = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-brand text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Briefcase" size={32} color="white" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Comprehensive Data Solutions for Growing Organizations
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
              From strategy to implementation, explore our vendor-neutral services designed for SMEs and mid-cap companies in finance, retail, healthcare, logistics, and public sector. Build your custom transformation package with transparent pricing and education-first approach.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="secondary"
                iconName="Settings"
                iconPosition="left"
                className="bg-white text-primary hover:bg-white/90"
              >
                Build Custom Package
              </Button>
              <Button
                variant="outline"
                iconName="Calculator"
                iconPosition="left"
                className="border-white text-white hover:bg-white/10"
                onClick={() => window.location.href = '/data-maturity-calculator-lead-generation-assessment'}
              >
                Take Maturity Assessment
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-6">
              Core Data Services
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Comprehensive data transformation services designed to take your organization from data chaos to strategic advantage. Each service includes detailed capability assessments and implementation timelines.
            </p>
          </div>

          <div className="space-y-6">
            {services?.map((service) => (
              <ServiceCard
                key={service?.id}
                service={service}
                isExpanded={expandedService === service?.id}
                onToggle={() => handleServiceToggle(service?.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Build Your Custom Solution Package */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Build Your Custom Solution Package</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Configure the perfect combination of services for your organization's unique needs
            </p>
          </div>
          <ServiceConfigurator />
        </div>
      </section>

      {/* Pricing Transparency */}
      <section className="py-16 px-4 bg-surface">
        <div className="max-w-7xl mx-auto">
          <PricingTransparency />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-brand text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">Ready to Transform Your Data Strategy?</h2>
          <p className="text-xl text-white/90 mb-8">
            Let's discuss how our services can accelerate your data transformation journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="secondary"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              className="bg-white text-primary hover:bg-white/90 hover-lift"
              onClick={() => navigate('/contact-multi-channel-engagement-hub')}
            >
              Book Consultation
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Calculator"
              iconPosition="left"
              className="border-white text-white hover:bg-white/10"
              onClick={() => navigate('/data-maturity-calculator-lead-generation-assessment')}
            >
              Free Assessment
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesOverview;